/**
 * CardEffectEditor — Edit parsed keywords, view effects, manage parse status
 */

import { useEffect, useState, useCallback } from 'react';
import * as AdminAPI from '../../services/AdminAPI';

interface CardEffectEditorProps {
  card: AdminAPI.CardDefinition;
  onClose: () => void;
  onUpdated: () => void;
}

const STATUS_BADGES: Record<string, string> = {
  auto: '🤖 Auto',
  verified: '✅ Verified',
  manual: '✋ Manual',
};

export function CardEffectEditor({ card, onClose, onUpdated }: CardEffectEditorProps) {
  const [keywords, setKeywords] = useState<string[]>(card.parsed_keywords ?? []);
  const [parseStatus, setParseStatus] = useState(card.parse_status || 'auto');
  const [availableKeywords, setAvailableKeywords] = useState<AdminAPI.KeywordInfo[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addingKeyword, setAddingKeyword] = useState('');

  useEffect(() => {
    void AdminAPI.listKeywords().then(setAvailableKeywords).catch(() => {});
  }, []);

  const showMessage = useCallback((msg: string) => {
    setMessage(msg);
    setTimeout(() => { setMessage(null); }, 3000);
  }, []);

  const handleReparse = useCallback(async () => {
    setIsLoading(true);
    try {
      await AdminAPI.reparseCard(card.code);
      showMessage('重新解析完成');
      onUpdated();
    } catch {
      showMessage('重新解析失敗');
    }
    setIsLoading(false);
  }, [card.code, onUpdated, showMessage]);

  const handleVerify = useCallback(async () => {
    setIsLoading(true);
    try {
      await AdminAPI.updateCardEffects(card.code, {
        parsed_keywords: keywords,
        parsed_effects: card.parsed_effects ?? { triggers: [], actions: [], keywords: [] },
        parse_status: 'verified',
      });
      setParseStatus('verified');
      showMessage('已標記為驗證');
      onUpdated();
    } catch {
      showMessage('操作失敗');
    }
    setIsLoading(false);
  }, [card, keywords, onUpdated, showMessage]);

  const handleSaveKeywords = useCallback(async () => {
    setIsLoading(true);
    try {
      await AdminAPI.updateCardEffects(card.code, {
        parsed_keywords: keywords,
        parsed_effects: card.parsed_effects ?? { triggers: [], actions: [], keywords: [] },
        parse_status: 'manual',
      });
      setParseStatus('manual');
      showMessage('關鍵字已更新');
      onUpdated();
    } catch {
      showMessage('儲存失敗');
    }
    setIsLoading(false);
  }, [card, keywords, onUpdated, showMessage]);

  const removeKeyword = (kw: string) => {
    setKeywords(prev => prev.filter(k => k !== kw));
  };

  const addKeyword = (kw: string) => {
    if (kw && !keywords.includes(kw)) {
      setKeywords(prev => [...prev, kw]);
    }
    setAddingKeyword('');
  };

  const parsedEffects = card.parsed_effects;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            ⚡ 效果編輯 — {card.name} ({card.code})
          </h3>
          <span style={styles.statusBadge}>
            {STATUS_BADGES[parseStatus] ?? parseStatus}
          </span>
        </div>

        {message && <div style={styles.message}>{message}</div>}

        {/* Effect Text (read-only) */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>效果文字</h4>
          <div style={styles.effectText}>{card.effect || '（無效果）'}</div>
        </div>

        {/* Keywords Editor */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>關鍵字</h4>
          <div style={styles.keywordList}>
            {keywords.map(kw => {
              const info = availableKeywords.find(k => k.id === kw);
              return (
                <span key={kw} style={styles.keywordChip}>
                  {info ? `${info.name} (${kw})` : kw}
                  <button style={styles.removeBtn} onClick={() => { removeKeyword(kw); }}>×</button>
                </span>
              );
            })}
            {keywords.length === 0 && <span style={styles.empty}>無關鍵字</span>}
          </div>

          {/* Add keyword */}
          <div style={styles.addRow}>
            <select
              style={styles.select}
              value={addingKeyword}
              onChange={(e) => { setAddingKeyword(e.target.value); }}
            >
              <option value="">選擇關鍵字...</option>
              {availableKeywords
                .filter(k => !keywords.includes(k.id))
                .map(k => (
                  <option key={k.id} value={k.id}>{k.name} ({k.id})</option>
                ))
              }
            </select>
            <button
              style={styles.addBtn}
              disabled={!addingKeyword}
              onClick={() => { addKeyword(addingKeyword); }}
            >
              ＋ 新增
            </button>
          </div>
        </div>

        {/* Parsed Effects (read-only viewer) */}
        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>解析結果</h4>
          {parsedEffects ? (
            <div style={styles.jsonViewer}>
              {parsedEffects.triggers && parsedEffects.triggers.length > 0 && (
                <div style={styles.effectRow}>
                  <strong>觸發條件:</strong>{' '}
                  {parsedEffects.triggers.map(t => (
                    <span key={t} style={styles.triggerChip}>{t}</span>
                  ))}
                </div>
              )}
              {parsedEffects.actions && parsedEffects.actions.length > 0 && (
                <div style={styles.effectRow}>
                  <strong>動作:</strong>
                  {parsedEffects.actions.map((a, i) => (
                    <div key={i} style={styles.actionItem}>
                      <span style={styles.actionType}>{a.type}</span>
                      {a.value !== undefined && a.value !== 0 && <span> 值: {a.value}</span>}
                      {a.keyword && <span> 關鍵字: {a.keyword}</span>}
                      {a.selector && (
                        <span style={styles.selectorInfo}>
                          {' '}[{a.selector.side} / {a.selector.zone}
                          {a.selector.count > 0 && ` ×${String(a.selector.count)}`}]
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(!parsedEffects.triggers || parsedEffects.triggers.length === 0) &&
               (!parsedEffects.actions || parsedEffects.actions.length === 0) && (
                <span style={styles.empty}>無解析結果</span>
              )}
            </div>
          ) : (
            <span style={styles.empty}>尚未解析</span>
          )}
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button
            style={styles.actionBtn}
            onClick={() => { void handleReparse(); }}
            disabled={isLoading}
          >
            🔄 重新解析
          </button>
          <button
            style={{ ...styles.actionBtn, backgroundColor: '#3a8a3a' }}
            onClick={() => { void handleVerify(); }}
            disabled={isLoading}
          >
            ✅ 標記驗證
          </button>
          <button
            style={{ ...styles.actionBtn, backgroundColor: '#8a6a3a' }}
            onClick={() => { void handleSaveKeywords(); }}
            disabled={isLoading}
          >
            💾 儲存關鍵字
          </button>
          <button style={styles.closeBtn} onClick={onClose}>
            關閉
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1001,
  },
  modal: {
    backgroundColor: '#252545', borderRadius: '12px', padding: '24px',
    width: '700px', maxHeight: '85vh', overflow: 'auto',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px',
  },
  title: { fontSize: '18px', margin: 0, color: '#ffffff' },
  statusBadge: {
    padding: '4px 12px', borderRadius: '12px', fontSize: '13px',
    backgroundColor: '#3a3a5a', color: '#cccccc',
  },
  message: {
    padding: '8px 12px', marginBottom: '12px', borderRadius: '6px',
    backgroundColor: '#1e3a1e', color: '#44ff44', fontSize: '13px',
  },
  section: { marginBottom: '16px' },
  sectionTitle: {
    fontSize: '14px', color: '#aaaaaa', margin: '0 0 8px 0',
    borderBottom: '1px solid #3a3a5a', paddingBottom: '4px',
  },
  effectText: {
    padding: '8px 12px', backgroundColor: '#1e1e3e', borderRadius: '6px',
    fontSize: '13px', lineHeight: '1.6', color: '#dddddd', whiteSpace: 'pre-wrap',
  },
  keywordList: {
    display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', minHeight: '32px',
  },
  keywordChip: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '4px 10px', backgroundColor: '#3a5a8a', borderRadius: '14px',
    fontSize: '12px', color: '#ffffff',
  },
  removeBtn: {
    background: 'none', border: 'none', color: '#ff8888', cursor: 'pointer',
    fontSize: '14px', padding: '0 2px', lineHeight: '1',
  },
  empty: { color: '#666666', fontSize: '13px', fontStyle: 'italic' },
  addRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  select: {
    flex: 1, padding: '6px 10px', fontSize: '13px', backgroundColor: '#2a2a4a',
    color: '#ffffff', border: '1px solid #3a3a5a', borderRadius: '4px',
  },
  addBtn: {
    padding: '6px 14px', fontSize: '13px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  jsonViewer: {
    padding: '8px 12px', backgroundColor: '#1e1e3e', borderRadius: '6px', fontSize: '13px',
  },
  effectRow: { marginBottom: '8px', color: '#cccccc' },
  triggerChip: {
    display: 'inline-block', padding: '2px 8px', margin: '0 4px',
    backgroundColor: '#5a3a7a', borderRadius: '10px', fontSize: '12px',
  },
  actionItem: {
    padding: '4px 0 4px 12px', borderLeft: '2px solid #3a3a5a', marginTop: '4px', color: '#bbbbbb',
  },
  actionType: {
    display: 'inline-block', padding: '1px 6px', backgroundColor: '#3a6a3a',
    borderRadius: '4px', fontSize: '11px', marginRight: '6px',
  },
  selectorInfo: { color: '#888888', fontSize: '11px' },
  actions: {
    display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px',
    borderTop: '1px solid #3a3a5a', paddingTop: '12px',
  },
  actionBtn: {
    padding: '8px 16px', fontSize: '13px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  closeBtn: {
    padding: '8px 16px', fontSize: '13px', backgroundColor: '#5a3a3a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
};
